/**
 * Page — the standard document-page scaffold.
 *
 * Composes PageHeader (breadcrumbs + canonical title/subtitle/actions band)
 * with PageContainer (body wrapper). This is the archetype for list and
 * content pages (Entities, People, Dashboard, …): a page declares *what* it
 * contains, and the header band + body rhythm are owned here — so the
 * per-page margin drift (mb-3/4/5/6/8/10, items-start vs items-end) that
 * accumulated across ~12 hand-rolled pages is eliminated.
 *
 * Sibling scaffolds for the other archetypes:
 *  - DetailPage                       — record detail (breadcrumbs + header
 *                                       slot + loading/error lifecycle)
 *  - PageContainer variant="viewport-lock" — Agent / Ask Canvas workspaces
 */
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { PageHeader, type Crumb } from './PageHeader'
import { PageContainer } from './PageContainer'

export type { Crumb }

export interface PageProps {
  /** Breadcrumb trail. Omit on top-level pages that don't need it. */
  crumbs?: Crumb[]
  title: ReactNode
  subtitle?: ReactNode
  /** Right-aligned header actions (buttons, links). */
  actions?: ReactNode
  /** Vertical alignment of the header row. Default `start`. */
  align?: 'start' | 'center' | 'end'
  /**
   * Body rhythm between direct children. Defaults to `none` so a page keeps
   * its own section margins when migrated; opt into `default` (space-y-6)
   * once a page's internal margins are removed.
   */
  spacing?: 'default' | 'tight' | 'none'
  /** Extra classes on the header wrapper. */
  headerClassName?: string
  /** Extra classes on the body wrapper. */
  className?: string
  /**
   * Where the page scrolls. `document` (default) is the ordinary page: the window scrolls, inside the app's padded <main>.
   *
   * `edge` is the listings' shell (the same one the Agency dashboard uses): the app's own full-bleed region (`viewport-lock`: from under the navbar and beside the
   * sidebar to the page's right and bottom edges). The header band stays fixed above; everything under it is the ONE page scroller, and it spans the FULL width of the
   * page, so its scrollbar sits at the page's right edge. The content padding (px-4 / sm:px-6 / lg:px-8, the app <main>'s own) lives INSIDE the scroller. Before, the
   * scrollbar was the window's while the table drew a second one inside the padding, and nothing kept the header in view.
   *
   * A table inside an `edge` page keeps its OWN bounded region (sticky header, both axes: that is what lets it scroll sideways and keep the header at once), so
   * it is the only nested scroller, and the page scroller takes over only when the chrome above the table is taller than the viewport leaves room for.
   */
  scroll?: 'document' | 'edge'
  children: ReactNode
}

export function Page({
  crumbs,
  title,
  subtitle,
  actions,
  align,
  spacing = 'none',
  headerClassName,
  className,
  scroll = 'document',
  children,
}: PageProps) {
  if (scroll === 'edge') {
    return (
      <PageContainer variant="viewport-lock" data-testid="page-viewport">
        <div className="mx-auto w-full max-w-[1600px] flex-none px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8" data-testid="page-header-band">
          <PageHeader crumbs={crumbs} title={title} subtitle={subtitle} actions={actions} align={align} className={headerClassName} />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto" data-testid="page-scroll">
          <PageContainer spacing={spacing} className={cn('mx-auto w-full max-w-[1600px] px-4 pb-10 sm:px-6 lg:px-8', className)}>
            {children}
          </PageContainer>
        </div>
      </PageContainer>
    )
  }
  return (
    <>
      <PageHeader
        crumbs={crumbs}
        title={title}
        subtitle={subtitle}
        actions={actions}
        align={align}
        className={headerClassName}
      />
      <PageContainer spacing={spacing} className={className}>
        {children}
      </PageContainer>
    </>
  )
}
